import { FC, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useProducts } from '@hooks/useProducts.ts';
import { useDisplayedProductsForTableAndChart } from '@hooks/useDisplayedProductsForTableAndChart';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface RetentionCycleData {
  cycle_number: number;
  products: Record<string, number>;
}

interface RetentionRateData {
  cycles: RetentionCycleData[];
  totals: Record<string, number>;
}

// Type for transformed table data
interface RetentionTableRow {
  cycle_number: string | number;
  products: Record<string, number>;
}

export const RetentionRate: FC = () => {
  // Data state
  const [reportData, setReportData] = useState<RetentionRateData | null>(null);
  const [loading, setLoading] = useState(false);
  const { products } = useProducts();

  // Use custom hooks for filter management
  const filters = useReportFilters({
    enableProducts: true,
    enableComparisons: false,
  });

  const { selectedItemsForDisplay } = useDisplayedProductsForTableAndChart(
    products,
    filters.selectedProducts
  );

  // Get selected product IDs; returns [] if none selected (backend treats [] as all products)
  const getProductIds = (): number[] => {
    if (filters.selectedProducts && filters.selectedProducts.length > 0) {
      return filters.selectedProducts
        .filter((id): id is string => id !== null && id !== undefined)
        .map((id) => parseInt(id, 10));
    }

    return [];
  };

  // Map product ID keys to product labels in a products record
  const mapProductKeysToLabels = (
    source?: Record<string, number>
  ): Record<string, number> => {
    if (!source) return {};
    return Object.entries(source).reduce(
      (acc, [productId, value]) => {
        const product = products.find((p) => p.value === productId);
        acc[product?.label ?? `Product #${productId}`] = value;
        return acc;
      },
      {} as Record<string, number>
    );
  };

  // Transform cycles data into table format
  const transformedTableData: RetentionTableRow[] = reportData
    ? [
        ...reportData.cycles.map((cycle) => ({
          cycle_number: cycle.cycle_number,
          products: cycle.products,
        })),
        {
          cycle_number: 'Total',
          products: reportData.totals,
        },
      ]
    : [];

  // Use custom hook for table columns generation
  const { columns, tableData } = useReportTableColumns({
    data: transformedTableData,
    selectedItems: selectedItemsForDisplay,
    itemsField: 'products',
    getAccessField: (item) => String(item.cycle_number),
    getMainDataField: (item, key) => item.products[key] || 0,
    valueFormatter: (_, value) => `${value.toLocaleString()}`,
    includeTotalsRow: false, // We handle totals manually
  });

  // Format date to ISO string (YYYY-MM-DD) using local timezone to avoid UTC offset issues
  const formatDate = (date: any): string | null => {
    if (!date) return null;
    if (typeof date === 'string') return date;
    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    return null;
  };

  const handleRunReport = async () => {
    try {
      setLoading(true);
      const productIds = getProductIds();
      const startDate = filters.valueStart?.startDate
        ? formatDate(filters.valueStart.startDate)
        : null;
      const endDate = filters.valueStart?.endDate
        ? formatDate(filters.valueStart.endDate)
        : null;

      const payload = {
        product_ids: productIds,
      };

      // Only add dates if they are set
      if (startDate) {
        Object.assign(payload, { start_date: startDate });
      }
      if (endDate) {
        Object.assign(payload, { end_date: endDate });
      }

      const response = await axios.post<RetentionRateData>(
        '/reports/retention-rate/',
        payload
      );

      const { cycles, totals } = response.data;
      const mappedCycles = cycles.map((cycle) => ({
        ...cycle,
        products: mapProductKeysToLabels(cycle.products),
      }));
      setReportData({ cycles: mappedCycles, totals: mapProductKeysToLabels(totals) });
      toast.success('Report generated successfully');
    } catch (error) {
      toast.error('Error fetching report');
      console.error('Error fetching report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadExcel = async () => {
    try {
      setLoading(true);
      const productIds = getProductIds();
      const startDate = filters.valueStart?.startDate
        ? formatDate(filters.valueStart.startDate)
        : null;
      const endDate = filters.valueStart?.endDate
        ? formatDate(filters.valueStart.endDate)
        : null;

      const payload = {
        product_ids: productIds,
      };

      // Only add dates if they are set
      if (startDate) {
        Object.assign(payload, { start_date: startDate });
      }
      if (endDate) {
        Object.assign(payload, { end_date: endDate });
      }

      const response = await axios.post(
        '/reports/retention-rate/export/',
        payload,
        {
          responseType: 'blob',
        }
      );

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');

      link.href = url;
      link.setAttribute('download', 'retention_rate.xlsx');
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('Report downloaded successfully');
    } catch (error) {
      toast.error('Error downloading report');
      console.error('Error downloading report:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">Retention Rate</h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={false}
          compareToSamePeriodPreviousYear={false}
          dateRangeValue={filters.valueStart}
          isLoading={loading}
          periodRange={filters.periodRange}
          productOptions={products}
          selectedProducts={filters.selectedProducts}
          showCompareToPreviousPeriod={false}
          showCompareToSamePeriodPreviousYear={false}
          showPeriodFilter={false}
          showProductsFilter={true}
          onDateRangeChange={filters.setValueStart}
          onProductsChange={filters.setSelectedProducts}
          onRunReport={handleRunReport}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Retention Data</h2>
          {tableData.length > 0 ? (
            <>
              <RadixTable columns={columns} data={tableData} />
              <Divider className="my-4" />
              <div className="flex justify-end">
                <Button
                  className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                  disabled={loading}
                  variant="outline"
                  onClick={handleDownloadExcel}
                >
                  {loading ? 'Downloading...' : 'Download Excel'}
                </Button>
              </div>
            </>
          ) : (
            <div className="text-center py-8 text-gray-400">
              {loading ? 'Loading report...' : 'No retention data available'}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
